import {
  type AskPunttAgentStep,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import {
  ArrowSquareOut,
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Database,
  Info,
  ListNumbers,
} from "@phosphor-icons/react";
import { Flex, Text, Tooltip } from "@radix-ui/themes";
import cx from "classnames";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";
import {
  type AskPunttArtifactContextValue,
  useAskPunttArtifactContext,
} from "../AskPunttArtifactContext";
import { useSharedNow } from "../hooks/useSharedNow";

/**
 * Tool names whose results land a chat-level artifact (ticket review or
 * canvas deliverable). Exported for chat.tsx's D8 streaming-suppression
 * predicate — when the in-flight tool_call is one of these, older
 * artifact rows grey out so a stale click can't yank the right pane
 * mid-stream. The clickability gate itself stays message-scoped (D7):
 * ANY tool_result inside a message that owns `viewTicketId` or a
 * non-empty `canvasContent` is eligible to be clickable, regardless of
 * whether the producing tool name is in this set.
 */
export const ARTIFACT_PRODUCING_TOOLS: ReadonlySet<string> = new Set([
  "create_ticket",
  "create_brief",
  "create_canvas",
  "create_roi",
  "create_research_brief",
]);

/**
 * The memory WRITE tool. Its chip is built from the tool CALL args, not the
 * result summary: what was remembered lives in the request. It is deliberately
 * absent from ARTIFACT_PRODUCING_TOOLS — a memory write opens no right-pane
 * artifact, so it must not grey out the ticket/canvas rows mid-stream.
 */
export const MEMORY_UPDATE_TOOL = "update_memory";

/**
 * Tools that rembrandt may still run / persist for the model, but that must
 * never render as agent-step chips (internal judgment helpers).
 */
export const HIDDEN_AGENT_STEP_TOOLS: ReadonlySet<string> = new Set([
  "craft_engagement_offer",
]);

// ─── Standardized tool/skill chip ─────────────────────────────────────────────

/**
 * Size + spacing shared by every stream chip. Rows that should match the chip
 * FOOTPRINT but keep their own fill/border/icons (e.g. review-checklist steps)
 * reuse this alone, so padding/rounding/gap live in one place and never drift.
 */
export const TOOL_CHIP_SIZING = "gap-2 rounded-lg px-[11px] py-[7px]";

/**
 * Standardized stream chip footprint without panel fill/stroke — same bare
 * convention as EnterpriseImpact / FeedMemoryCard. Callers append state
 * classes (active/blocked/hover) after this base.
 */
export const TOOL_CHIP_CLASS = `flex w-full min-w-0 @container items-center ${TOOL_CHIP_SIZING} bg-transparent`;

/**
 * Primary name + optional secondary detail for tool chips.
 * Always one line: name truncates; secondary is inline when the chip is
 * ≥240px wide, otherwise an Info tooltip on the far right.
 * Class names must be complete static strings for Tailwind JIT.
 */
function ToolChipLabels({
  name,
  secondary,
  mid,
}: {
  name: string;
  secondary?: string;
  /** Compact mid content (e.g. result count) — always visible, never wraps. */
  mid?: ReactNode;
}) {
  const hasSecondary = !!secondary?.trim();
  return (
    <>
      <Text
        size="1"
        weight="regular"
        title={name}
        data-testid="agent-step-name"
        className={
          hasSecondary
            ? "min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-11 @[240px]:max-w-[45%] @[240px]:flex-none"
            : "min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-11"
        }
      >
        {name}
      </Text>
      {mid}
      {hasSecondary ? (
        <>
          <Text
            size="1"
            title={secondary}
            data-testid="agent-step-secondary-inline"
            className="hidden min-w-0 flex-1 truncate whitespace-nowrap text-puntt-neutral-gray-9 @[240px]:block"
          >
            {secondary}
          </Text>
          <Tooltip content={secondary}>
            <span
              data-testid="agent-step-secondary-info"
              className="ml-auto inline-flex shrink-0 text-puntt-neutral-gray-8 @[240px]:hidden"
              aria-label={secondary}
            >
              <Info size={14} weight="regular" aria-hidden />
            </span>
          </Tooltip>
        </>
      ) : null}
    </>
  );
}

/** Shared chrome for in-stream status accordion headers and bodies. */
const STATUS_PANEL_TOGGLE_BASE =
  "flex w-full items-center gap-2 px-2.5 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-puntt-accent-7";

/** Muted panels (AgentSteps, SearchProgress): transparent header — no fill/stroke. */
export const STATUS_PANEL_TOGGLE_MUTED_CLASS = `${STATUS_PANEL_TOGGLE_BASE} bg-transparent`;

/** Review checklist: white shell with a white header row (frame 02). */
export const STATUS_PANEL_TOGGLE_LIGHT_CLASS = `${STATUS_PANEL_TOGGLE_BASE} bg-base-white hover:bg-base-white`;

/** @deprecated Use STATUS_PANEL_TOGGLE_MUTED_CLASS or STATUS_PANEL_TOGGLE_LIGHT_CLASS. */
export const STATUS_PANEL_TOGGLE_CLASS = STATUS_PANEL_TOGGLE_MUTED_CLASS;

// why: bare body matches EnterpriseImpact / FeedMemoryCard — no divider stroke or fill.
export const STATUS_PANEL_ITEMS_MUTED_CLASS = "bg-transparent px-1.5 py-1.5";

export const STATUS_PANEL_ITEMS_LIGHT_CLASS =
  "border-t border-puntt-neutral-gray-4 bg-base-white px-1.5 py-1.5";

/** @deprecated Use STATUS_PANEL_ITEMS_MUTED_CLASS or STATUS_PANEL_ITEMS_LIGHT_CLASS. */
export const STATUS_PANEL_ITEMS_CLASS = STATUS_PANEL_ITEMS_MUTED_CLASS;

interface AgentStepsProps {
  steps: AskPunttAgentStep[];
  isStreaming: boolean;
  /**
   * The parent assistant message. When omitted (legacy callers, isolated
   * tests), artifact clickability is disabled and the component behaves
   * exactly as it did pre-feature.
   */
  message?: AskPunttMessage;
}

// ─── Step pair / group types ─────────────────────────────────────────────────

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type StepGroup = {
  tool: string;
  displayName: string;
  statusName?: string;
  pairs: StepPair[];
  /** Timestamp of first call — stable key across re-renders */
  firstTimestamp: string;
};

/**
 * Groups consecutive same-tool call+result pairs into StepGroups.
 * Thinking/text steps are skipped (rendered as chat bubbles by ChatMessage).
 *
 * Rembrandt often converts a `tool_call` into a `tool_result` in place (same
 * array slot). Those lone results must still render as completed chips —
 * skipping them hid finished tools, and a stale unpaired `tool_call` from a
 * longer local merge left chips spinning forever after the turn settled.
 */
export function groupConsecutiveToolSteps(
  steps: AskPunttAgentStep[],
): StepGroup[] {
  const result: StepGroup[] = [];
  let i = 0;

  while (i < steps.length) {
    const step = steps[i];

    if (step.type === "thinking" || step.type === "text") {
      i++;
      continue;
    }

    if (
      (step.type === "tool_call" || step.type === "tool_result") &&
      HIDDEN_AGENT_STEP_TOOLS.has(step.tool)
    ) {
      i++;
      continue;
    }

    if (step.type === "tool_call") {
      const group: StepGroup = {
        tool: step.tool ?? "",
        displayName: step.displayName ?? "",
        statusName: step.statusName,
        pairs: [],
        firstTimestamp: step.timestamp,
      };

      while (i < steps.length) {
        const current = steps[i];
        if (current.type === "tool_call" && current.tool === step.tool) {
          const pair: StepPair = {
            call: current as AskPunttAgentStep & { type: "tool_call" },
          };
          i++;
          if (i < steps.length && steps[i].type === "tool_result") {
            pair.result = steps[i] as AskPunttAgentStep & {
              type: "tool_result";
            };
            i++;
          }
          group.pairs.push(pair);
        } else {
          break;
        }
      }

      result.push(group);
      continue;
    }

    if (step.type === "tool_result") {
      const asResult = step as AskPunttAgentStep & { type: "tool_result" };
      result.push({
        tool: step.tool ?? "",
        displayName: step.displayName ?? "",
        statusName: step.statusName,
        pairs: [
          {
            call: {
              ...asResult,
              type: "tool_call",
            } as AskPunttAgentStep & { type: "tool_call" },
            result: asResult,
          },
        ],
        firstTimestamp: step.timestamp,
      });
      i++;
      continue;
    }

    i++;
  }

  return result;
}

function streamingSummaryLabel(
  groups: StepGroup[],
  t: (key: string) => string,
): string {
  if (groups.length === 0) return t("agentSteps.workingFallback");

  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    const lastPair = group.pairs[group.pairs.length - 1];
    if (lastPair && !lastPair.result) {
      return (
        group.statusName || group.displayName || t("agentSteps.workingFallback")
      );
    }
  }

  const lastGroup = groups[groups.length - 1];
  return (
    lastGroup.statusName ||
    lastGroup.displayName ||
    t("agentSteps.workingFallback")
  );
}

// ─── Clickability derivation ─────────────────────────────────────────────────

interface Clickability {
  kind: "ticket" | "canvas";
  isActive: boolean;
  isBlocked: boolean;
  ariaLabel: string;
  testId: string;
  onActivate: () => void;
}

function messageHasCanvas(m: AskPunttMessage | undefined | null): boolean {
  const c = m?.canvasContent as { blocks?: unknown[] } | null | undefined;
  return !!(c && Array.isArray(c.blocks) && c.blocks.length > 0);
}

function formatStepTime(ts: string | undefined): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/**
 * Decide whether a tool_result row maps to a re-openable artifact. Returns
 * null when no provider context exists, no parent message, or the row
 * doesn't qualify. The clickability gate is message-scoped (D7): any
 * tool_result inside a message that owns `viewTicketId` or canvas content
 * is eligible, including single-artifact chats (cowork sidebar).
 */
function deriveClickability(
  step: AskPunttAgentStep & { type: "tool_result" },
  message: AskPunttMessage | undefined,
  ctx: AskPunttArtifactContextValue | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): Clickability | null {
  if (!message || !ctx) return null;

  const reviewId = message.viewReviewId;
  const ticketId = message.viewTicketId;
  const isReviewResult =
    step.tool === "create_ticket" && !!(reviewId || ticketId);

  const canActivateV2Review =
    ctx.onActivateReview != null || (ctx.allReviewIds?.length ?? 0) > 0;

  if (isReviewResult && reviewId && canActivateV2Review) {
    const allReviewIds = ctx.allReviewIds ?? [];
    const idx = allReviewIds.indexOf(reviewId);
    const total = allReviewIds.length;
    const isActive = ctx.activeReviewId === reviewId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive
      ? t("agentSteps.viewingVerb")
      : t("agentSteps.openVerb");
    const positionLabel =
      idx >= 0 ? t("agentSteps.positionLabel", { idx: idx + 1, total }) : "";
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? t("agentSteps.createdTimeLabel", { time }) : "";
    const blockLabel = isBlocked ? t("agentSteps.generatingBlockLabel") : "";
    return {
      kind: "ticket",
      isActive,
      isBlocked,
      ariaLabel: t("agentSteps.ticketAriaLabel", {
        verb,
        position: positionLabel,
        time: timeLabel,
        block: blockLabel,
      }),
      testId: `agent-step-toolresult-review-${reviewId}`,
      onActivate: () => {
        if (ctx.openArtifact) {
          ctx.openArtifact(
            { kind: "askPunttReview", reviewId },
            "message_chip",
          );
        } else if (ctx.onActivateReview) {
          ctx.onActivateReview(reviewId);
        }
      },
    };
  }

  if (isReviewResult && ticketId) {
    const idx = ctx.allReviewTicketIds.indexOf(ticketId);
    const total = ctx.allReviewTicketIds.length;
    const isActive = ctx.activeTicketId === ticketId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive
      ? t("agentSteps.viewingVerb")
      : t("agentSteps.openVerb");
    const positionLabel =
      idx >= 0 ? t("agentSteps.positionLabel", { idx: idx + 1, total }) : "";
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? t("agentSteps.createdTimeLabel", { time }) : "";
    const blockLabel = isBlocked ? t("agentSteps.generatingBlockLabel") : "";
    return {
      kind: "ticket",
      isActive,
      isBlocked,
      ariaLabel: t("agentSteps.ticketAriaLabel", {
        verb,
        position: positionLabel,
        time: timeLabel,
        block: blockLabel,
      }),
      testId: `agent-step-toolresult-ticket-${ticketId}`,
      onActivate: () => {
        if (ctx.openArtifact) {
          ctx.openArtifact({ kind: "review", ticketId }, "message_chip");
        } else {
          ctx.onActivateTicket(ticketId);
        }
      },
    };
  }

  if (messageHasCanvas(message)) {
    const messageId = message._id;
    const isActive = ctx.activeCanvasMessageId === messageId;
    const isBlocked = ctx.isAnotherArtifactStreaming && !isActive;
    const verb = isActive
      ? t("agentSteps.viewingVerb")
      : t("agentSteps.openVerb");
    // D5 fallback chain: displayName → summary → "canvas"
    const rawName =
      step.displayName || step.summary || t("agentSteps.canvasFallbackName");
    const name = rawName.length > 60 ? `${rawName.slice(0, 60)}...` : rawName;
    const time = formatStepTime(step.timestamp);
    const timeLabel = time ? t("agentSteps.createdTimeLabel", { time }) : "";
    const blockLabel = isBlocked ? t("agentSteps.generatingBlockLabel") : "";
    return {
      kind: "canvas",
      isActive,
      isBlocked,
      ariaLabel: t("agentSteps.canvasAriaLabel", {
        verb,
        name,
        time: timeLabel,
        block: blockLabel,
      }),
      testId: `agent-step-toolresult-canvas-${messageId}`,
      onActivate: () => {
        if (ctx.openArtifact) {
          ctx.openArtifact({ kind: "canvas", messageId }, "message_chip");
        } else {
          ctx.onActivateCanvas(messageId);
        }
      },
    };
  }

  return null;
}

/**
 * Used by AgentSteps to decide whether to default-expand the steps
 * accordion (D1). True when at least one tool_result is clickable AND
 * its artifact is NOT currently active — i.e. the user could navigate
 * to it. We deliberately ignore blocked rows so an in-flight new
 * artifact doesn't force-expand the older message's accordion.
 */
function groupsHaveUnviewedClickable(
  groups: StepGroup[],
  message: AskPunttMessage | undefined,
  ctx: AskPunttArtifactContextValue | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): boolean {
  for (const g of groups) {
    for (const pair of g.pairs) {
      if (!pair.result) continue;
      const c = deriveClickability(pair.result, message, ctx, t);
      if (c && !c.isActive && !c.isBlocked) return true;
    }
  }
  return false;
}

// ─── Individual step renderers ────────────────────────────────────────────────

function ToolCallStep({
  step,
  settled = false,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
  /** The parent message finished streaming: a call with no recorded result can
   * never resolve anymore, so it must not keep spinning. */
  settled?: boolean;
}) {
  const primaryName = step.statusName || step.displayName;
  const showOriginal = !!step.statusName;
  const secondaryText = step.description
    ? step.description
    : step.args
      ? JSON.stringify(step.args).slice(0, 80)
      : "";

  const detail =
    [showOriginal ? step.displayName : null, secondaryText || null]
      .filter(Boolean)
      .join(" · ") || undefined;

  return (
    <Flex
      align="center"
      gap="2"
      className={TOOL_CHIP_CLASS}
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      {settled ? (
        <CheckCircle
          size={14}
          weight="fill"
          className="shrink-0 text-puntt-neutral-gray-8"
          aria-hidden
        />
      ) : (
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          aria-hidden
        />
      )}
      <ToolChipLabels name={primaryName} secondary={detail} />
    </Flex>
  );
}

function CreateTicketProgressStep({
  step,
}: {
  step: AskPunttAgentStep & { type: "tool_call" };
}) {
  const { t } = useTranslation("ui");
  const startMs = useMemo(
    () => new Date(step.timestamp).getTime(),
    [step.timestamp],
  );
  // Shared ticker across all active tool chips — one interval, not N.
  const nowMs = useSharedNow(500);
  const elapsed = Math.floor((nowMs - startMs) / 1000);

  const pct = Math.min(Math.round((elapsed / 10) * 85), 85);
  const primaryName = step.statusName || step.displayName;

  return (
    <Flex
      direction="column"
      gap="2"
      className={TOOL_CHIP_CLASS}
      data-testid="agent-step"
      data-step-type="tool_call"
    >
      <Flex align="center" gap="2" className="w-full min-w-0">
        <CircleNotch
          size={14}
          className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          aria-hidden
        />
        <Text
          size="1"
          title={primaryName}
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-11"
        >
          {primaryName}
        </Text>
        <Text
          size="1"
          className="ml-auto shrink-0 tabular-nums text-puntt-neutral-gray-8"
        >
          {t("agentSteps.elapsedSeconds", { n: elapsed })}
        </Text>
      </Flex>
      <div className="h-1 w-full overflow-hidden rounded-full bg-puntt-neutral-gray-3">
        <div
          className="h-full rounded-full bg-puntt-accent-7 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </Flex>
  );
}

function MemoryUpdateStep({
  call,
  done,
}: {
  call: AskPunttAgentStep & { type: "tool_call" };
  /** A tool_result arrived (or the message settled): stop spinning. */
  done: boolean;
}) {
  const { t } = useTranslation("ui");
  const args = (call.args ?? {}) as {
    content?: unknown;
    corrects_node_id?: unknown;
  };
  const content = typeof args.content === "string" ? args.content.trim() : "";
  const isCorrection =
    typeof args.corrects_node_id === "string" && !!args.corrects_node_id.trim();
  const rowId = call.stepId || call.timestamp;

  const name = !done
    ? t("agentSteps.memorySavingName")
    : isCorrection
      ? t("agentSteps.memoryCorrectedName")
      : t("agentSteps.memorySavedName");
  const summary =
    content.length > 100 ? `${content.slice(0, 100)}...` : content;

  return (
    <Flex
      align="center"
      justify="between"
      gap="2"
      // why: Figma AgentSteps memoryCollapsed 49613:1054 — Database + title +
      // instruction + CaretRight; hover fills muted panel (49865:1067).
      className="group flex w-full min-w-0 items-center gap-2.5 rounded-xl p-2 hover:bg-puntt-neutral-gray-2"
      data-testid={`agent-step-memory-update-${rowId}`}
      data-step-type={done ? "tool_result" : "tool_call"}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2.5">
        {done ? (
          <Database
            size={16}
            className="mt-0.5 shrink-0 text-puntt-neutral-gray-11"
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={16}
            className="mt-0.5 shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          data-testid="agent-step-name"
          className="shrink-0 whitespace-nowrap text-puntt-neutral-gray-11"
        >
          {name}
        </Text>
        {summary ? (
          <Text
            size="1"
            title={summary}
            data-testid="agent-step-secondary-inline"
            className="min-w-0 truncate text-puntt-neutral-gray-12"
          >
            {summary}
          </Text>
        ) : null}
      </div>
      <CaretRight
        size={16}
        className="shrink-0 text-puntt-neutral-gray-11"
        aria-hidden
      />
    </Flex>
  );
}

function ToolResultStep({
  step,
  clickability,
}: {
  step: AskPunttAgentStep & { type: "tool_result" };
  clickability: Clickability | null;
}) {
  const { t } = useTranslation("ui");
  const summary =
    step.summary.length > 100
      ? `${step.summary.slice(0, 100)}...`
      : step.summary;
  const primaryName = step.statusName || step.displayName;

  // D9: 200ms click flash, suppressed under prefers-reduced-motion.
  const [justClicked, setJustClicked] = useState(false);
  useEffect(() => {
    if (!justClicked) return;
    const t = setTimeout(() => setJustClicked(false), 200);
    return () => clearTimeout(t);
  }, [justClicked]);

  const resultCountMid =
    step.resultCount !== undefined && step.resultCount > 0 ? (
      <Text
        size="1"
        className="shrink-0 whitespace-nowrap text-puntt-neutral-gray-9"
      >
        {t("agentSteps.resultCount", { count: step.resultCount })}
      </Text>
    ) : null;

  if (!clickability) {
    return (
      <Flex
        align="center"
        gap="2"
        className={TOOL_CHIP_CLASS}
        data-testid="agent-step"
        data-step-type="tool_result"
      >
        <CheckCircle
          size={14}
          weight="fill"
          className="shrink-0 text-malachite-700"
          aria-hidden
        />
        <ToolChipLabels
          name={primaryName}
          secondary={summary || undefined}
          mid={resultCountMid}
        />
      </Flex>
    );
  }

  const { isActive, isBlocked, ariaLabel, testId, onActivate, kind } =
    clickability;

  const handleClick = () => {
    if (isBlocked || isActive) return;
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReducedMotion) setJustClicked(true);
    onActivate();
  };

  const stateClass = isActive
    ? "border border-puntt-accent-9 bg-puntt-neutral-gray-2"
    : isBlocked
      ? "cursor-not-allowed opacity-60"
      : `cursor-pointer hover:bg-puntt-accent-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-7 ${
          justClicked ? "bg-puntt-accent-3" : ""
        }`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isBlocked}
      tabIndex={isBlocked ? -1 : 0}
      aria-disabled={isBlocked || undefined}
      aria-current={isActive ? "true" : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
      data-step-type="tool_result"
      data-artifact-kind={kind}
      data-artifact-active={isActive ? "true" : "false"}
      data-artifact-blocked={isBlocked ? "true" : "false"}
      className={`${TOOL_CHIP_CLASS} text-left transition-colors ${stateClass}`}
    >
      <CheckCircle
        size={14}
        weight="fill"
        className="shrink-0 text-malachite-700"
        aria-hidden
      />
      <ToolChipLabels
        name={primaryName}
        secondary={summary || undefined}
        mid={resultCountMid}
      />
      {!isActive && (
        <ArrowSquareOut
          size={12}
          weight="regular"
          className={cx(
            "shrink-0 text-puntt-neutral-gray-8",
            summary ? "" : "ml-auto",
          )}
          aria-hidden
        />
      )}
    </button>
  );
}

// ─── Collapsed group row ──────────────────────────────────────────────────────

/** Settled (non-streaming) unpaired tool_call → completed chip, never spin. */
function settledResultFromCall(
  call: AskPunttAgentStep & { type: "tool_call" },
): AskPunttAgentStep & { type: "tool_result" } {
  return {
    ...call,
    type: "tool_result",
    summary: "",
    resultCount: undefined,
  };
}

function CollapsedToolGroup({
  group,
  isNew,
  message,
  ctx,
  isStreaming,
}: {
  group: StepGroup;
  isNew: boolean;
  message: AskPunttMessage | undefined;
  ctx: AskPunttArtifactContextValue | null;
  isStreaming: boolean;
}) {
  const { t } = useTranslation("ui");
  const [expanded, setExpanded] = useState(false);

  const callCount = group.pairs.length;
  const primaryName = group.statusName || group.displayName;
  const lastPair = group.pairs[group.pairs.length - 1];
  // Once the turn is settled, unpaired calls are treated as done so we never
  // leave "Update Chat Details" (etc.) spinning under a completed summary.
  const allDone = !!lastPair?.result || !isStreaming;

  // Build summary label: show first description/query if available
  const firstCall = group.pairs[0]?.call;
  const firstQuery = firstCall?.description
    ? firstCall.description.slice(0, 30)
    : firstCall?.args
      ? JSON.stringify(firstCall.args).slice(0, 30)
      : null;

  const label =
    callCount === 1
      ? primaryName
      : firstQuery
        ? t("agentSteps.groupSummaryWithQuery", {
            primaryName,
            query: firstQuery,
            count: callCount - 1,
          })
        : t("agentSteps.groupSummaryWithCount", {
            primaryName,
            count: callCount,
          });

  const renderPair = (pair: StepPair) => {
    if (pair.call.tool === MEMORY_UPDATE_TOOL) {
      return (
        <MemoryUpdateStep
          call={pair.call}
          done={!!pair.result || !isStreaming}
        />
      );
    }
    if (pair.result) {
      return (
        <ToolResultStep
          step={pair.result}
          clickability={deriveClickability(pair.result, message, ctx, t)}
        />
      );
    }
    if (!isStreaming) {
      const settled = settledResultFromCall(pair.call);
      return (
        <ToolResultStep
          step={settled}
          clickability={deriveClickability(settled, message, ctx, t)}
        />
      );
    }
    if (pair.call.tool === "create_ticket") {
      return <CreateTicketProgressStep step={pair.call} />;
    }
    return <ToolCallStep step={pair.call} />;
  };

  if (callCount === 1) {
    const onlyPair = lastPair;
    return (
      <div
        data-testid="agent-step-group"
        className={isNew ? "animate-block-fade-up" : ""}
      >
        {renderPair(onlyPair)}
      </div>
    );
  }

  // Multiple same-tool pairs — collapsed summary with expand toggle
  return (
    <div
      data-testid="agent-step-group"
      className={isNew ? "animate-block-fade-up" : ""}
    >
      <button
        data-testid="agent-steps-button-291"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-puntt-neutral-gray-3"
        aria-expanded={expanded}
      >
        {allDone ? (
          <CheckCircle
            size={14}
            weight="fill"
            className="shrink-0 text-malachite-700"
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
          />
        )}
        <Text size="2" className="flex-1 truncate text-puntt-neutral-gray-11">
          {label}
        </Text>
        {expanded ? (
          <CaretDown
            size={10}
            className="shrink-0 text-puntt-neutral-gray-11"
          />
        ) : (
          <CaretRight
            size={10}
            className="shrink-0 text-puntt-neutral-gray-11"
          />
        )}
      </button>

      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
          expanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="ml-4 space-y-0.5 border-l border-puntt-neutral-gray-3 pl-2">
          {group.pairs.map((pair) => (
            <div key={pair.call.timestamp}>{renderPair(pair)}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main AgentSteps component ────────────────────────────────────────────────

export function AgentSteps({ steps, isStreaming, message }: AgentStepsProps) {
  const { t } = useTranslation("ui");
  const ctx = useAskPunttArtifactContext();
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const prevGroupCountRef = useRef(0);

  const groups = useMemo(() => groupConsecutiveToolSteps(steps), [steps]);

  // D1: when a message holds a clickable artifact the user hasn't viewed
  // yet, default the accordion to expanded post-stream so the affordance
  // is reachable without a manual click. Manual collapse wins (the
  // `manualToggle !== null` short-circuit) — once the user explicitly
  // toggles, we stop force-expanding.
  const hasUnviewedClickable = useMemo(
    () => groupsHaveUnviewedClickable(groups, message, ctx, t),
    [groups, message, ctx, t],
  );

  const expanded =
    manualToggle !== null ? manualToggle : isStreaming || hasUnviewedClickable;

  const prevGroupCount = prevGroupCountRef.current;
  useEffect(() => {
    prevGroupCountRef.current = groups.length;
  }, [groups.length]);

  // Count logical tool operations (call+result pairs), not raw step rows —
  // otherwise a merged accordion of 6 tools that each have call+result shows
  // "12 Steps" instead of "6 Steps".
  const stepCount = groups.reduce((n, g) => n + g.pairs.length, 0);
  const summaryText = isStreaming
    ? streamingSummaryLabel(groups, t)
    : t("agentSteps.stepCount", { count: stepCount });

  // Nothing to show if all steps were thinking (rendered as chat bubbles)
  if (groups.length === 0 && !isStreaming) return null;

  return (
    <div className="my-3" data-testid="agent-steps">
      <div
        className={cx(
          "overflow-hidden",
          interactiveSurfaceClasses({ expandable: true, muted: true }),
        )}
      >
        <button
          data-testid="agent-steps-toggle"
          type="button"
          onClick={() => setManualToggle(!expanded)}
          aria-expanded={expanded}
          aria-label={t(
            expanded
              ? "agentSteps.collapseAriaLabel"
              : "agentSteps.expandAriaLabel",
          )}
          className={STATUS_PANEL_TOGGLE_MUTED_CLASS}
        >
          {isStreaming ? (
            <CircleNotch
              size={16}
              className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
              aria-hidden
            />
          ) : (
            <ListNumbers
              size={16}
              className="shrink-0 text-puntt-neutral-gray-12"
              aria-hidden
            />
          )}
          <Text
            size="1"
            weight="regular"
            className="text-puntt-neutral-gray-12"
          >
            {summaryText}
          </Text>
          <CaretDown
            size={14}
            aria-hidden
            className={`ml-auto shrink-0 text-puntt-neutral-gray-12 transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
          />
        </button>

        {expanded && groups.length > 0 && (
          <div
            data-testid="agent-steps-items"
            className={STATUS_PANEL_ITEMS_MUTED_CLASS}
          >
            <div className="flex flex-col gap-1.5">
              {groups.map((group, idx) => (
                <CollapsedToolGroup
                  key={group.firstTimestamp}
                  group={group}
                  isNew={idx >= prevGroupCount}
                  message={message}
                  ctx={ctx}
                  isStreaming={isStreaming}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
